import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { observer } from "mobx-react";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { usePatient, useCreateOrUpdatePatient } from "../../stores/Patients";

const InsuranceDataLiftoverDialog: React.FC = observer(() => {
  const patientStore = useStore(PatientsContext);
  const { insuranceDataLiftoverDialogOpen } = patientStore;
  const {
    open,
    patientId,
    rxInsureeNumber,
    patientInsuranceNo,
    rxPayer,
    patientPayer,
    rxInsuranceStatus,
    patientInsuranceStatus,
  } = insuranceDataLiftoverDialogOpen;

  const { data: patient } = usePatient(patientId!, open && !!patientId);
  const createOrUpdatePatient = useCreateOrUpdatePatient();
  const [saving, setSaving] = useState(false);

  const insureeNumberChanged =
    !!rxInsureeNumber &&
    (!patientInsuranceNo ||
      patientInsuranceNo.trim() !== rxInsureeNumber.trim());
  const payerChanged =
    !!rxPayer?.id && (!patientPayer?.id || patientPayer.id !== rxPayer.id);
  const insuranceStatusChanged =
    !!rxInsuranceStatus &&
    (!patientInsuranceStatus ||
      patientInsuranceStatus.trim() !== rxInsuranceStatus.trim());

  const handleCancel = () => {
    patientStore.setInsuranceDataLiftoverDialogOpen(false, null);
  };

  const handleConfirm = async () => {
    if (!patient) return;

    setSaving(true);
    try {
      await createOrUpdatePatient.mutateAsync({
        ...patient,
        ...(insureeNumberChanged ? { insuranceNo: rxInsureeNumber } : {}),
        ...(payerChanged ? { payer: rxPayer } : {}),
        ...(insuranceStatusChanged ? { status: rxInsuranceStatus } : {}),
      } as any);
      handleCancel();
    } catch {
      // Error handled by mutation's onError
    } finally {
      setSaving(false);
    }
  };

  if (
    !open ||
    !patientId ||
    (!insureeNumberChanged && !payerChanged && !insuranceStatusChanged)
  ) {
    return null;
  }

  const lines: string[] = [];
  if (insureeNumberChanged) {
    lines.push(
      !patientInsuranceNo
        ? `Versichertennummer: Das Rezept enthält ${rxInsureeNumber}, beim Patienten ist keine hinterlegt.`
        : `Versichertennummer: Das Rezept enthält ${rxInsureeNumber}, beim Patienten ist ${patientInsuranceNo} hinterlegt.`,
    );
  }
  if (payerChanged) {
    const rxPayerLabel = rxPayer?.name
      ? `${rxPayer.name} (${rxPayer.id})`
      : rxPayer?.id;
    const patientPayerLabel = patientPayer?.name
      ? `${patientPayer.name} (${patientPayer.id})`
      : patientPayer?.id;
    lines.push(
      !patientPayer?.id
        ? `Kostenträger: Das Rezept enthält ${rxPayerLabel}, beim Patienten ist keiner hinterlegt.`
        : `Kostenträger: Das Rezept enthält ${rxPayerLabel}, beim Patienten ist ${patientPayerLabel} hinterlegt.`,
    );
  }
  if (insuranceStatusChanged) {
    lines.push(
      !patientInsuranceStatus
        ? `Status: Das Rezept enthält ${rxInsuranceStatus}, beim Patienten ist keiner hinterlegt.`
        : `Status: Das Rezept enthält ${rxInsuranceStatus}, beim Patienten ist ${patientInsuranceStatus} hinterlegt.`,
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="insurance-liftover-dialog-title"
    >
      <DialogTitle id="insurance-liftover-dialog-title">
        Versicherungsdaten übernehmen
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="insurance-liftover-dialog-description">
          {lines.map((line, i) => (
            <span key={i} style={{ display: "block", marginBottom: 8 }}>
              {line}
            </span>
          ))}
          <span style={{ display: "block", marginTop: 8 }}>
            Möchtest du diese Daten für den Patienten übernehmen?
          </span>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={saving}>
          Nein
        </Button>
        <Button onClick={handleConfirm} disabled={saving} variant="contained">
          Ja, übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default InsuranceDataLiftoverDialog;
